import { useEffect, useState } from "react"

import { Button, Dialog, Input } from "@productlane/ui"

import { api } from "@/lib/api"

type Step = "enter" | "available" | "sent"

const CHECK_MESSAGES = {
	taken: "Another account already uses this email.",
	same: "This is already your email.",
	invalid: "Enter a valid email address.",
} as const

/** Moves the account to a new address: check it is free, then mail a confirmation link. */
export function ChangeEmailDialog({
	isOpen,
	onOpenChange,
	currentEmail,
}: {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	currentEmail: string
}) {
	const [email, setEmail] = useState("")
	const [step, setStep] = useState<Step>("enter")
	const [error, setError] = useState<string | null>(null)
	const check = api.user.checkEmailChange.useMutation()
	const request = api.user.requestEmailChange.useMutation()

	useEffect(() => {
		if (!isOpen) return
		setEmail("")
		setStep("enter")
		setError(null)
	}, [isOpen])

	const onCheck = async () => {
		setError(null)
		try {
			const { status } = await check.mutateAsync({ email })
			if (status === "available") setStep("available")
			else setError(CHECK_MESSAGES[status])
		} catch (e) {
			setError(e instanceof Error ? e.message : "Something went wrong.")
		}
	}

	const onSend = async () => {
		setError(null)
		try {
			await request.mutateAsync({ email })
			setStep("sent")
		} catch (e) {
			setError(e instanceof Error ? e.message : "Something went wrong.")
		}
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title="Change email"
			footer={
				<div className="flex w-full items-center justify-end gap-2">
					{step === "sent" ? (
						<Button onClick={() => onOpenChange(false)}>Done</Button>
					) : (
						<>
							<Button variant="outline" onClick={() => onOpenChange(false)}>
								Cancel
							</Button>
							{step === "available" ? (
								<Button isLoading={request.isLoading} onClick={onSend}>
									Send confirmation link
								</Button>
							) : (
								<Button
									isLoading={check.isLoading}
									disabled={email.trim().length === 0}
									onClick={onCheck}
								>
									Check email
								</Button>
							)}
						</>
					)}
				</div>
			}
		>
			{step === "sent" ? (
				<p className="text-sm text-secondary">
					We sent a confirmation link to{" "}
					<span className="font-medium text-primary">{email.trim()}</span>. It
					expires in 1 hour. Your email stays {currentEmail} until you open it.
				</p>
			) : (
				<form
					className="flex flex-col gap-3"
					onSubmit={(event) => {
						event.preventDefault()
						if (step === "available") void onSend()
						else if (email.trim()) void onCheck()
					}}
				>
					<p className="text-sm text-secondary">
						The new email applies to every workspace you&apos;re in. We&apos;ll
						send a link to confirm it, then sign you out on every device.
					</p>
					<div className="flex flex-col gap-1.5">
						<Input
							autoFocus
							type="email"
							name="new-email"
							placeholder="New email address"
							value={email}
							onChange={(event) => {
								setEmail(event.target.value)
								setStep("enter")
								setError(null)
							}}
						/>
						{error ? (
							<p className="text-xs text-red-600 dark:text-red-400">{error}</p>
						) : step === "available" ? (
							<p className="text-xs text-green-700 dark:text-green-400">
								This email is free to use.
							</p>
						) : null}
					</div>
				</form>
			)}
		</Dialog>
	)
}
